import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { updatePharmacyProfile, fetchPharmacyProfile } from '../../../redux/middleware/user/pharmacy/profile';
import { requestPhoneOTP, verifyPhoneOTP } from '../../../redux/middleware/user/pharmacy/verification';
import {
  Stepper,
  Step,
  StepLabel,
  StepIcon,
  StepIconComponent,
  Typography,
  Button,
  Box,
  TextField,
  CircularProgress,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Grid,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  Tooltip,
  ListItemText,
  Divider,
  Select
} from '@mui/material';
import { ErrorOutline, Facebook, Twitter, LinkedIn, Instagram, Add, Delete, Phone, Email, LocationOn, Business, Schedule, People, Work, MiscellaneousServices, CloudUpload, Language, Assignment, CheckCircle } from '@mui/icons-material';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { defaultCountries, FlagImage, parseCountry, usePhoneInput, PhoneInput } from 'react-international-phone';

import 'react-international-phone/style.css';
import PharmacyDetailsStep from '../../../components/pharmacy/form/profile/PharmacyDetailsStep';
import ContactInformationStep from '../../../components/pharmacy/form/profile/ContactInformationStep ';
import LicensingCertificationStep from '../../../components/pharmacy/form/profile/LicensingCertificationStep ';
import OperatingHoursStep from '../../../components/pharmacy/form/profile/OperatingHoursStep ';
import SocialMediaStep from '../../../components/pharmacy/form/profile/SocialMediaStep ';
import ServicesOfferedStep from '../../../components/pharmacy/form/profile/ServicesOfferedStep ';
import StaffDirectoryStep from '../../../components/pharmacy/form/profile/StaffDirectoryStep ';
import ReviewDetailsStep from '../../../components/pharmacy/form/profile/ReviewDetailsStep ';

const steps = [
  'Pharmacy Details',
  'Contact Information',
  'Licensing & Certifications',
  'Operating Hours',
  'Social Media',
  'Services Offered',
  'Staff',
  'Review & Submit'
];

const validationSchema = Yup.object({
  pharmacy_name: Yup.string().required('Pharmacy Name is required'),
  owner_name: Yup.string().required('Owner Name is required'),
  email: Yup.string().email('Invalid email format').required('Email is required'),
  phone_number: Yup.string().required('Phone Number is required'),
  bio: Yup.string().required('Bio is required'),
  license_number: Yup.string().required('License Number is required'),
  operating_hours: Yup.object({
    days_open: Yup.array()
      .of(Yup.string())
      .min(1, 'Days Open is required')
      .required('Operating hours days are required'),
    weekdays: Yup.object({
      start_time: Yup.string().required('Opening Time is required'),
      end_time: Yup.string().required('Closing Time is required')
    }),
    weekends: Yup.object({
      start_time: Yup.string().required('Opening Time is required'),
      end_time: Yup.string().required('Closing Time is required')
    })
  }),
  services_offered: Yup.array()
    .min(1, 'At least one service is required')
    .of(
      Yup.object({
        name: Yup.string().required('Service Name is required'),
        description: Yup.string()
      })
    ),
  staff_directory: Yup.array().of(
    Yup.object({
      name: Yup.string(),
      position: Yup.string()
    })
  )
});

const ProfileCompletionWizard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, loading } = useSelector((state) => state.pharmacyProfile);
  const profileCompletion = useSelector((state) => state.pharmacyProfile.profileCompletion);
  const { isVerified, error } = useSelector((state) => state.pharmacyVerification);
  const [logoPreview, setLogoPreview] = useState(null);
  const [filePreviews, setFilePreviews] = useState({});
  const [activeStep, setActiveStep] = useState(0);
  const [progress, setProgress] = useState(activeStep === steps.length ? 100 : (activeStep / steps.length) * 100);
  const [isOTPRequested, setIsOTPRequested] = useState(false);
  const [otp, setOTP] = useState('');
  const [otpError, setOtpError] = useState(null);
  const [formData, setFormData] = useState({
    pharmacy_name: '',
    owner_name: '',
    email: '',
    phone_number: '',
    bio: '',
    address: {
      street: '',
      city: '',
      state: '',
      country: '',
      postal_code: '',
      gps_coordinates: ''
    },
    contact_info: {
      additional_phones: '',
      website: '',
      emergency_contact: ''
    },
    license_number: '',
    operating_hours: {
      days_open: [],
      weekdays: { start_time: '', end_time: '' },
      weekends: { start_time: '', end_time: '' },
      emergency_services: false
    },
    services_offered: [],
    staff_directory: [],
    social_media: {
      facebook: '',
      twitter: '',
      linkedin: '',
      instagram: ''
    },
    logo: null,
    licenses: [],
    certifications: [],
    services_offered: [],
    staff_directory: []
  });
  const pharmacyLogin = useSelector((state) => state.pharmacyLogin);
  const pharmacyProfile = useSelector((state) => state.pharmacyProfile);

  // Console log the entire pharmacyLogin state
  useEffect(() => {
    console.log('Pharmacy Login State:', pharmacyLogin);
    console.log('Pharmacy profile State:', pharmacyProfile);
  }, [pharmacyLogin]);

  useEffect(() => {
    const savedData = JSON.parse(localStorage.getItem('pharmacyProfile'));
    if (savedData) {
      setFormData(savedData);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('pharmacyProfile', JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    dispatch(updatePharmacyProfile());
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      setFormData((prevData) => ({
        ...prevData,
        pharmacy_name: user.pharmacy_name || prevData.pharmacy_name,
        owner_name: user.owner_name || prevData.owner_name,
        email: user.email || prevData.email,
        phone_number: user.phone_number || prevData.phone_number,
        license_number: user.license_number || prevData.license_number,
        address: user.address || prevData.address,
        contact_info: user.contact_info || prevData.contact_info,
        operating_hours: user.operating_hours || prevData.operating_hours,
        services_offered: user.services_offered || prevData.services_offered,
        staff_directory: user.staff_directory || prevData.staff_directory,
        social_media: user.social_media || prevData.social_media,
        logo: user.logo || prevData.logo
      }));
    }
  }, [user]);

  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
    defaultCountry: 'us',
    value: formData.phone_number,
    countries: defaultCountries,
    onChange: (data) => {
      setFormData({ ...formData, phone_number: data.phone });
    }
  });

  const formik = useFormik({
    initialValues:
    {
      ...formData,
      services_offered: formData.services_offered || [],
      staff_directory: formData.staff_directory || [],
      address: formData.address || {
        street: '',
        city: '',
        state: '',
        country: '',
        postal_code: '',
        gps_coordinates: ''
      }
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      handleSubmit(values);
    }
  });

  const handleSubmit = async (values) => {
    const formData = new FormData();
    const defaultData = {
      operating_hours: values.operating_hours || {
        weekdays: { start_time: '', end_time: '' },
        weekends: { start_time: '', end_time: '' },
        days_open: []
      },
      services_offered: values.services_offered || [],
      contact_info: values.contact_info || {
        additional_phones: '',
        website: '',
        emergency_contact: ''
      },
      staff_directory: values.staff_directory || [],
      social_media: values.social_media || {
        facebook: '',
        twitter: '',
        linkedin: '',
        instagram: ''
      },
      licenses: values.licenses || [],
      certifications: values.certifications || []
    };

    const ensureDate = (date) => date && date !== 'null' ? date : new Date().toISOString().split('T')[0];
    const updatedValues = {
      ...defaultData,
      ...values,
      licenses: values.licenses.map((item) => ({
        id: item.id || null, // Keep ID for existing items
        name: item.name,
        issued_by: item.issued_by || 'Unknown',
        issue_date: ensureDate(item.issue_date), // Default if missing
        expiry_date: item.expiry_date || '',
        file: item.file instanceof File ? item.file : null // Send file only if it's a File object
      })),
      certifications: values.certifications.map((item) => ({
        id: item.id || null,
        name: item.name,
        issued_by: item.issued_by || 'Unknown',
        issue_date: ensureDate(item.issue_date),
        expiry_date: item.expiry_date || '',
        file: item.file instanceof File ? item.file : null
      }))
    };

    Object.keys(updatedValues).forEach((key) => {
      if (key === 'logo' && updatedValues[key] instanceof File) {
        formData.append(key, updatedValues[key]); // Upload logo
      } else if (key === 'licenses' || key === 'certifications') {
        updatedValues[key].forEach((item, index) => {
          formData.append(`${key}[${index}][name]`, item.name);
          formData.append(`${key}[${index}][issued_by]`, item.issued_by);
          formData.append(`${key}[${index}][issue_date]`, item.issue_date);
          formData.append(`${key}[${index}][expiry_date]`, item.expiry_date || '');

          if (item.id) {
            formData.append(`${key}[${index}][id]`, item.id); // Keep existing items
          }
          if (item.file) {
            formData.append(`${key}[${index}][file]`, item.file); // Only attach actual File objects
          }
        });
      } else if (typeof updatedValues[key] === 'object' && updatedValues[key] !== null) {
        formData.append(key, JSON.stringify(updatedValues[key])); // Convert objects/arrays to JSON
      } else {
        formData.append(key, updatedValues[key]);
      }
    });
    console.log('Submitting Profile Data:', updatedValues);

    try {
      console.log(' Dispatching profile update...');

      // Step 1: Dispatch the profile update action and get the response
      const updateResponse = await dispatch(updatePharmacyProfile(user, formData));
      console.log('user details:', user);
      console.log('Update response:', updateResponse);

      // Ensure the update was successful
      if (!updateResponse || updateResponse.error) {
        console.error(' Update failed:', updateResponse?.error);
        toast.error('Profile update failed');
        return;
      }

      console.log('Fetching updated profile...');

      // Step 2: Fetch the updated profile from backend
      const updatedProfileResponse = await dispatch(fetchPharmacyProfile());
      console.log('Fetched updated profile:', updatedProfileResponse);

      // Ensure profile data is valid
      if (!updatedProfileResponse ||
        updatedProfileResponse.error ||
        !updatedProfileResponse.payload) {
        console.error(' Failed to fetch updated profile:', updatedProfileResponse);
        toast.error('Could not retrieve updated profile');
        return;
      }

      // Step 3: Check profile completion and navigate if 100%
      const profileCompletion = updatedProfileResponse.payload.profile_completion;
      if (profileCompletion === 100) {
        toast.success('Profile Update successful!');
        console.log('Profile completion is 100. Redirecting...');
        navigate('/app/pharmacy/dashboardpage');
      } else {
        console.log('⚠️ Profile completion is NOT 100:', profileCompletion);
        toast.warning(`Profile is only ${profileCompletion}% complete`);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('An unexpected error occurred');
    }
  };

  useEffect(() => {
    console.log('useEffect Triggered. Profile Completion:', profileCompletion); // Debugging

    if (profileCompletion === 100) {
      console.log('Redirecting to Dashboard...');
    }
  }, [profileCompletion]);

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (!files.length) return;

    const newFile = files[0]; // Get the first file

    if (name === 'logo') {
      //  Set the pharmacy logo
      formik.setFieldValue(name, newFile);

      //  Generate preview for logo
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
      setLogoPreview(URL.createObjectURL(newFile));
    } else if (name === 'licenses' || name === 'certifications') {
      //  Generate default values if missing
      const capitalized = name.charAt(0).toUpperCase() + name.slice(1);
      const newItem = {
        name: formik.values[`new${capitalized}`]?.name || 'Unnamed',
        issued_by: formik.values[`new${capitalized}`]?.issued_by || 'Unknown',
        issue_date: formik.values[`new${capitalized}`]?.issue_date || new Date().toISOString().split('T')[0],
        expiry_date: formik.values[`new${capitalized}`]?.expiry_date || '',
        expiry_date: formik.values[`new${capitalized}`]?.expiry_date || '',
        file: newFile
      };

      //  Update formik state
      formik.setFieldValue(name, [...(formik.values[name] || []), newItem]);

      //  Generate and store file preview
      const previewKey = `${newFile.name}-${Date.now()}`;
      setFilePreviews((prev) => ({
        ...prev,
        [previewKey]: URL.createObjectURL(newFile)
      }));

      //  Reset form fields
      formik.setFieldValue(`new${capitalized}`, { name: '', issued_by: '', issue_date: '', expiry_date: '' });
    }
  };

  //  Prevent memory leaks by revoking unused object URLs
  useEffect(() => {
    return () => {
      Object.values(filePreviews).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [filePreviews]);

  const removeFile = (name, index) => {
    const updatedFiles = [...formik.values[name]];
    updatedFiles.splice(index, 1); // Remove the file at the given index
    formik.setFieldValue(name, updatedFiles);
  };

  const addItem = (field, item) => {
    formik.setFieldValue(field, [...formik.values[field], item]);
  };

  const removeItem = (field, index) => {
    const updatedItems = [...formik.values[field]];
    updatedItems.splice(index, 1);
    formik.setFieldValue(field, updatedItems);
  };

  // Ensure cleanup of old previews to avoid memory leaks
  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const removeLogo = () => {
    formik.setFieldValue('logo', null); // Clear logo in formik
    setLogoPreview(null); // Reset preview
  };

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      formik.validateForm().then((errors) => {
        if (Object.keys(errors).length === 0) {
          console.log('Submitting final form:', formik.values);
          formik.handleSubmit();
          setProgress(100);
        } else {
          console.error('Validation failed:', errors);
          toast.error('Please fix validation errors before submitting.');
        }
      });
    } else {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  useEffect(() => {
    const saveDraft = setTimeout(() => {
      if (formData !== JSON.parse(localStorage.getItem('pharmacyProfile'))) {
        localStorage.setItem('pharmacyProfile', JSON.stringify(formData));
      }
    }, 2000);

    return () => clearTimeout(saveDraft);
  }, [formData]);

  const handleSendOTP = () => {
    if (!formik.values.phone_number || formik.values.phone_number.length < 10) {
      toast.error('Enter a valid phone number before requesting OTP.');
      return;
    }
    dispatch(requestPhoneOTP(formik.values.phone_number));
    setIsOTPRequested(true);
  };

  const handleVerifyOTP = () => {
    if (!otp) {
      toast.error('Please enter the OTP before verifying.');
      return;
    }
    dispatch(verifyPhoneOTP(formik.values.phone_number, otp));
  };

  const socialMediaPlatforms = [
    { name: 'facebook', label: 'Facebook', icon: <Facebook sx={{ color: '#1877F2' }} /> },
    { name: 'twitter', label: 'Twitter', icon: <Twitter sx={{ color: '#1DA1F2' }} /> },
    { name: 'linkedin', label: 'LinkedIn', icon: <LinkedIn sx={{ color: '#0077B5' }} /> },
    { name: 'instagram', label: 'Instagram', icon: <Instagram sx={{ color: '#E1306C' }} /> }
  ];

  const textFieldStyles = {
    backgroundColor: '#d4d4d4',
    marginTop: '1em',
    borderRadius: '5px',
    borderBottom: '1.5px solid #bdd1e0',
    height: { xs: '2.5em', sm: '2.7em' },
    '& .MuiOutlinedInput-root': {
      '& fieldset': { height: { xs: '2.8em', sm: '3em' } },
      '&:hover fieldset': { borderColor: '#6794b6' },
      '&.Mui-focused fieldset': { borderColor: '#3b76a2', height: { xs: '2.8em', sm: '3em' } }
    },
    '& .MuiInputLabel-root': { color: '#13334c', fontSize: { xs: '0.9em', sm: '1em' } },
    '& .MuiInputLabel-root.Mui-focused': { color: '#13334c' },
    '& .MuiOutlinedInput-input': { color: '#000000', fontSize: { xs: '0.95em', sm: '1em' }, padding: { xs: '0.85em 0.5em', sm: '0.7em 0.5em' } }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '900px',
        margin: '40px auto',
        padding: '30px',
        backgroundColor: '#ffffff',
        borderRadius: '10px',
        boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.2)'
      }}
    >

      <Typography variant='h4' align='center' sx={{ fontWeight: 600, color: '#1976d2', mb: 2 }}>
        Complete Your Pharmacy Profile
      </Typography>

      <LinearProgress
        variant='determinate'
        value={progress}
        sx={{
          height: 8,
          borderRadius: 5,
          backgroundColor: '#e0e0e0',
          '& .MuiLinearProgress-bar': {
            backgroundColor: '#1976d2'
          }
        }}
      />
      <Typography align='center' sx={{ fontWeight: 500, mb: 2, color: '#1976d2' }}>
        {Math.round(progress)}% Complete
      </Typography>

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: { xs: 2, sm: 3 }, px: { xs: 0, sm: 1 }, display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
        {steps.map((label, index) => (
          <Step key={index}>
            <StepLabel
              StepIconComponent={(props) => (
                <StepIcon {...props} sx={{ color: props.active ? '#1976d2' : '#8a8a8a', fontSize: { xs: '1.5rem', sm: '2rem' } }} />
              )}
              sx={{
                '& .MuiStepLabel-label': {
                  color: activeStep === index ? '#1976d2' : '#8a8a8a',
                  fontWeight: activeStep === index ? 'bold' : 'normal',
                  fontSize: { xs: '0.75rem', sm: '0.9rem' }
                },
                '& .MuiStepIcon-root': {
                  color: activeStep === index ? '#1976d2' : '#8a8a8a',
                  fontSize: { xs: '1.5rem', sm: '2rem' }
                },
                '& .MuiStepConnector-root': {
                  top: 10,
                  left: 'calc(-50% + 16px)',
                  right: 'calc(50% + 16px)'
                },
                '& .Mui-active .MuiStepIcon-text': {
                  fill: '#fff'
                },
                '& .Mui-completed .MuiStepIcon-root': {
                  color: '#1976d2 !important'
                },
                '& .Mui-completed .MuiStepIcon-text': {
                  fill: '#fff'
                }
              }}
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ mt: 3, color: '#13334c' }}>
        {loading
          ? (
            <CircularProgress />
            )
          : (
            <form onSubmit={formik.handleSubmit} sx={{ color: '#13334c' }}>

              {activeStep === 0 && (
                <PharmacyDetailsStep
                  formik={formik}
                  logoPreview={logoPreview}
                  handleFileChange={handleFileChange}
                  removeLogo={removeLogo}
                  textFieldStyles={textFieldStyles}
                />
              )}

              {activeStep === 1 && (
                <ContactInformationStep
                  formik={formik}
                  inputValue={inputValue}
                  handlePhoneValueChange={handlePhoneValueChange}
                  inputRef={inputRef}
                  country={country}
                  setCountry={setCountry}
                  defaultCountries={defaultCountries}
                  parseCountry={parseCountry}
                  FlagImage={FlagImage}
                  loading={loading}
                  isVerified={isVerified}
                  handleSendOTP={handleSendOTP}
                  isOTPRequested={isOTPRequested}
                  otp={otp}
                  setOTP={setOTP}
                  otpError={otpError}
                  handleVerifyOTP={handleVerifyOTP}
                />
              )}

              {activeStep === 2 && (
                <LicensingCertificationStep
                  formik={formik}
                  handleFileChange={handleFileChange}
                  addItem={addItem}
                  removeItem={removeItem}
                  filePreviews={filePreviews}
                  textFieldStyles={textFieldStyles}
                />
              )}

              {activeStep === 3 && (
                <OperatingHoursStep
                  formik={formik}
                  textFieldStyles={textFieldStyles}
                />
              )}

              {activeStep === 4 && (
                <SocialMediaStep
                  formik={formik}
                  textFieldStyles={textFieldStyles}
                  socialMediaPlatforms={socialMediaPlatforms}
                />
              )}

              {activeStep === 5 && (
                <ServicesOfferedStep
                  formik={formik}
                  textFieldStyles={textFieldStyles}
                  addItem={addItem}
                  removeItem={removeItem}
                />
              )}

              {activeStep === 6 && (
                <StaffDirectoryStep
                  formik={formik}
                  textFieldStyles={textFieldStyles}
                  addItem={addItem}
                  removeItem={removeItem}
                />
              )}

              {activeStep === 7 && (
                <ReviewDetailsStep
                  formik={formik}
                  logoPreview={logoPreview}
                />
              )}

              <Box sx={{ mt: 3 }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                  sx={{
                    backgroundColor: '#3A6E9E',
                    color: '#ffffff',
                    '&:hover': { backgroundColor: '#2B5A83' },
                    borderRadius: '6px',
                    padding: '10px 20px'
                  }}
                >
                  Back
                </Button>
                <Button
                  variant='contained'
                  sx={{
                    backgroundColor: '#3A6E9E',
                    color: '#ffffff',
                    '&:hover': { backgroundColor: '#ccc', color: '#3A6E9E', borderColor: '#3A6E9E' },
                    borderRadius: '6px',
                    padding: '10px 20px',
                    ml: 2,
                    mr: 2
                  }}
                  onClick={handleNext}
                >
                  {activeStep === steps.length - 1 ? 'Submit' : 'Next'}
                </Button>
                <Button
                  variant='outlined'
                  onClick={() => localStorage.setItem('pharmacyDraft', JSON.stringify(formData))}
                  sx={{
                    borderColor: '#3A6E9E',
                    color: '#3A6E9E',
                    '&:hover': { backgroundColor: '#c4c4c4' },
                    borderRadius: '6px',
                    padding: '10px 20px'
                  }}
                >
                  Save as Draft
                </Button>
              </Box>
            </form>
            )}
      </Box>
    </Box>
  );
};

export default ProfileCompletionWizard;
